'use client';

import type { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { coerceCostSummary, coerceUsageSummary } from '@/lib/ai/costs';
import { formatUsd, USD_SMART_CENTS_THRESHOLDS } from '@/lib/formatters/currency';
import { cn, formatLocaleNumber } from '@/lib/utils';
import type { AICostSummary, AIUsageSummary } from '@/types/ai';

interface ModelTelemetryTooltipProperties {
  children: ReactNode;
  className?: string | undefined;
  cost?: Partial<AICostSummary> | null | undefined;
  enabled?: boolean | undefined;
  extraContent?: ReactNode | undefined;
  tooltipClassName?: string | undefined;
  usage?: Partial<AIUsageSummary> | null | undefined;
}

export function ModelTelemetryTooltip({
  children,
  usage,
  cost,
  enabled = true,
  className,
  tooltipClassName,
  extraContent,
}: ModelTelemetryTooltipProperties) {
  const normalizedUsage = coerceUsageSummary(usage ?? null);
  const normalizedCost = coerceCostSummary(cost ?? null);

  const hasUsage = Boolean(
    normalizedUsage &&
    (normalizedUsage.promptTokens > 0 || normalizedUsage.completionTokens > 0 || normalizedUsage.totalTokens > 0)
  );
  const hasCost = Boolean(
    normalizedCost &&
    (typeof normalizedCost.totalCostCents === 'number' ||
      typeof normalizedCost.inputCostCents === 'number' ||
      typeof normalizedCost.outputCostCents === 'number')
  );

  if (!enabled) {
    return <span className={className}>{children}</span>;
  }

  return (
    <TooltipProvider delayDuration={120} skipDelayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex cursor-help border-b border-dotted border-muted-foreground/60 bg-transparent p-0 text-left align-baseline outline-none focus-visible:ring-1 focus-visible:ring-primary/50',
              className
            )}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent
          className={cn(
            'z-layer-tooltip max-h-[calc(100vh-32px)] max-w-xs overflow-y-auto text-xs space-y-1',
            tooltipClassName
          )}
        >
          {hasUsage ? (
            <div>
              <div>Prompt tokens: {formatLocaleNumber(normalizedUsage?.promptTokens ?? 0)}</div>
              <div>Completion tokens: {formatLocaleNumber(normalizedUsage?.completionTokens ?? 0)}</div>
              <div>Total tokens: {formatLocaleNumber(normalizedUsage?.totalTokens ?? 0)}</div>
              {typeof normalizedUsage?.reasoningTokens === 'number' ? (
                <div>Reasoning tokens: {formatLocaleNumber(normalizedUsage.reasoningTokens)}</div>
              ) : null}
            </div>
          ) : null}
          {hasCost ? (
            <div>
              <div>
                Total cost:{' '}
                {formatUsd((normalizedCost?.totalCostCents ?? 0) / 100, {
                  thresholds: USD_SMART_CENTS_THRESHOLDS,
                  defaultFractionDigits: 4,
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                })}
              </div>
              {typeof normalizedCost?.inputCostCents === 'number' ? (
                <div className="text-muted-foreground/80">
                  Input cost:{' '}
                  {formatUsd(normalizedCost.inputCostCents / 100, {
                    thresholds: USD_SMART_CENTS_THRESHOLDS,
                    defaultFractionDigits: 4,
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  })}
                </div>
              ) : null}
              {typeof normalizedCost?.outputCostCents === 'number' ? (
                <div className="text-muted-foreground/80">
                  Output cost:{' '}
                  {formatUsd(normalizedCost.outputCostCents / 100, {
                    thresholds: USD_SMART_CENTS_THRESHOLDS,
                    defaultFractionDigits: 4,
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4,
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
          {hasUsage || hasCost || extraContent != null ? null : (
            <div className="text-muted-foreground/80">Telemetry not yet available for this run.</div>
          )}
          {extraContent == null ? null : <div>{extraContent}</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
